"use client";

import {
	type PitchRowFx,
	type PitchStatusKey,
	type TierNumber,
	earnedMediaGaps,
	pitchList,
	pitchRows,
	pitchStatusCounts,
	pitchSummary,
	placedNudge,
} from "@marketing/shared/lib/bento-fixtures";
import { AipvsTierBadge } from "@saas/publisher-lookup/components/AipvsTierBadge";
import { Badge } from "@ui/components/badge";
import { cn } from "@ui/lib";
import {
	ArrowDownIcon,
	ArrowRightIcon,
	CheckIcon,
	ClipboardPasteIcon,
	EyeOffIcon,
	FileDownIcon,
	LockIcon,
	SearchIcon,
	SparklesIcon,
	TargetIcon,
	TelescopeIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { Fragment, type ReactNode } from "react";
import { useRevealed } from "./motion";

/**
 * Media Lists marketing tiles (Pitch Lists in the app). Every visual mirrors
 * the shipped product (tier pills via the product's AipvsTierBadge, the status
 * vocabulary, the Placed suggestion, the read-only share page) on fictional
 * fixture data. Chrome and prose read from `marketing.mediaList.widgets`.
 *
 * "Blocks AI crawlers" is shown by shape and text (dashed outline, eye-off
 * icon), never by a positive/negative color: it is a property of the outlet,
 * not a metric that moved.
 */

const NS = "marketing.mediaList.widgets";

// ── Small shared pieces ─────────────────────────────────────────────────────

/** AIPVS score + the product's tier pill. */
export function ScoredTier({
	score,
	tier,
	className,
}: {
	score: number;
	tier: TierNumber;
	className?: string;
}) {
	return (
		<span
			className={cn(
				"inline-flex items-center gap-1.5 whitespace-nowrap",
				className,
			)}
		>
			<span className="font-mono text-sm font-semibold tabular-nums text-foreground">
				{score}
			</span>
			<AipvsTierBadge tier={tier} />
		</span>
	);
}

/** Shape + text for an outlet AI can't read. */
export function BlocksAiLabel({ className }: { className?: string }) {
	const t = useTranslations(NS);
	return (
		<span
			className={cn(
				"inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-dashed border-muted-foreground/60 px-2 py-px text-[11px] font-semibold text-muted-foreground",
				className,
			)}
		>
			<EyeOffIcon className="size-3" aria-hidden />
			{t("blocksAi")}
		</span>
	);
}

/**
 * Same treatment as the product: Target is a plain outline, Pitched info,
 * Placed success, Declined muted text (a "no" is a normal outcome).
 */
export function PitchStatusChip({ status }: { status: PitchStatusKey }) {
	const t = useTranslations(NS);
	const label = t(`status.${status}`);
	const base =
		"inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold leading-tight";
	if (status === "pitched") {
		return (
			<Badge status="info" className="px-2.5 py-0.5 normal-case">
				{label}
			</Badge>
		);
	}
	if (status === "placed") {
		return (
			<Badge status="success" className="px-2.5 py-0.5 normal-case">
				{label}
			</Badge>
		);
	}
	if (status === "declined") {
		return (
			<span className={cn(base, "text-muted-foreground")}>{label}</span>
		);
	}
	return <span className={cn(base, "border text-foreground")}>{label}</span>;
}

function PublisherMark({ name }: { name: string }) {
	return (
		<span
			className="flex size-7 shrink-0 items-center justify-center rounded-md border bg-background text-[11px] font-bold text-muted-foreground"
			aria-hidden
		>
			{name.replace(/^The /, "").charAt(0)}
		</span>
	);
}

// ── Hero: the pitch list table ──────────────────────────────────────────────

/**
 * Table columns once the tile is wide enough (container query, not viewport:
 * the tile's width depends on the page grid, not the screen).
 */
const TABLE_COLS =
	"@xl:grid-cols-[minmax(0,1fr)_3.75rem_2.5rem_7rem_3.25rem_4.75rem]";

/** One value: labeled on its own when stacked, a bare table cell when wide. */
function Metric({
	label,
	align = "right",
	children,
}: {
	label: string;
	align?: "left" | "right";
	children: ReactNode;
}) {
	return (
		<div
			className={cn(
				"flex min-w-0 flex-col gap-0.5",
				align === "right" && "@xl:items-end",
			)}
		>
			<span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground @xl:hidden">
				{label}
			</span>
			{children}
		</div>
	);
}

function PitchTableRow({ row, index }: { row: PitchRowFx; index: number }) {
	const t = useTranslations(NS);
	const revealed = useRevealed();
	return (
		<li
			className={cn(
				"grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 border-t px-4 py-2.5 motion-safe:transition-all motion-safe:duration-500",
				TABLE_COLS,
				revealed
					? "motion-safe:translate-y-0 motion-safe:opacity-100"
					: "motion-safe:translate-y-1 motion-safe:opacity-0",
			)}
			style={{ transitionDelay: `${150 + index * 90}ms` }}
		>
			<div className="flex min-w-0 items-center gap-2.5">
				<PublisherMark name={row.name} />
				<div className="min-w-0">
					<p className="truncate text-sm font-medium text-foreground">
						{row.name}
					</p>
					{row.blocksAi ? (
						<BlocksAiLabel className="mt-0.5" />
					) : (
						<p className="truncate text-xs text-muted-foreground">
							{row.domain}
						</p>
					)}
				</div>
			</div>

			{/* Status: right end of the name line when stacked, last column when wide. */}
			<div className="col-start-2 row-start-1 justify-self-end @xl:col-start-6 @xl:justify-self-start">
				<PitchStatusChip status={row.status} />
			</div>

			{/* Metrics: a labeled row under the name when stacked; four cells when wide. */}
			<div className="col-span-2 grid grid-cols-[3.25rem_1.75rem_6.5rem_auto] gap-x-3 @sm:grid-cols-[4rem_2.5rem_7.5rem_auto] @sm:gap-x-4 @sm:pl-[2.375rem] @xl:contents">
				<Metric label={t("colTraffic")}>
					<span className="font-mono text-sm tabular-nums text-foreground">
						{row.traffic}
					</span>
				</Metric>
				<Metric label={t("colDr")}>
					<span className="font-mono text-sm tabular-nums text-foreground">
						{row.domainRank}
					</span>
				</Metric>
				<Metric label={t("colAipvs")} align="left">
					<ScoredTier score={row.aipvs.score} tier={row.aipvs.tier} />
				</Metric>
				<Metric label={t("colCited")}>
					<span
						className={cn(
							"font-mono text-sm tabular-nums",
							row.citations === 0
								? "text-muted-foreground"
								: "font-semibold text-foreground",
						)}
					>
						{row.citations}
					</span>
				</Metric>
			</div>
		</li>
	);
}

/** Compact pitch list, sorted by traffic, the way a list usually starts. */
export function PitchListHeroTable({
	rows = pitchRows,
	className,
}: {
	rows?: PitchRowFx[];
	className?: string;
}) {
	const t = useTranslations(NS);
	return (
		<div className={cn("@container", className)}>
			<div className="flex flex-wrap items-end justify-between gap-2 px-4 pt-4 pb-3">
				<div className="min-w-0">
					<p className="text-xs font-semibold text-muted-foreground">
						{t("tableEyebrow", { brand: pitchList.brand })}
					</p>
					<p className="truncate text-base font-bold tracking-tight">
						{t("listName")}
					</p>
				</div>
				<span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
					{t("sortedBy")}
					<ArrowDownIcon className="size-3" aria-hidden />
				</span>
			</div>
			<div
				className={cn(
					"hidden items-center gap-x-3 bg-muted/40 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground @xl:grid",
					TABLE_COLS,
				)}
			>
				<span>{t("colPublisher")}</span>
				<span className="text-right">{t("colTraffic")}</span>
				<span className="text-right">{t("colDr")}</span>
				<span>{t("colAipvs")}</span>
				<span className="text-right">{t("colCited")}</span>
				<span>{t("colStatus")}</span>
			</div>
			<ul>
				{rows.map((row, i) => (
					<PitchTableRow key={row.id} row={row} index={i} />
				))}
			</ul>
			<p className="border-t px-4 py-2.5 text-[11px] text-muted-foreground">
				{t("tableFootnote", {
					brand: pitchList.brandShort,
					days: pitchList.windowDays,
				})}
			</p>
		</div>
	);
}

// ── Blind spot: same list, two outlets side by side ─────────────────────────

/** The two rows that make the point: biggest outlet vs the one AI reads. */
export function BlindSpotCompare({ className }: { className?: string }) {
	const t = useTranslations(NS);
	const big = pitchRows.find((r) => r.id === "daily-tamarack");
	const read = pitchRows.find((r) => r.id === "longtrail-review");
	if (!big || !read) return null;
	return (
		<div className={cn("@container", className)}>
			<div className="grid gap-4 @lg:grid-cols-2">
				{[big, read].map((row) => (
					<div
						key={row.id}
						className={cn(
							"rounded-2xl border bg-card p-5",
							row.blocksAi && "border-dashed",
						)}
					>
						<div className="flex items-center gap-2.5">
							<PublisherMark name={row.name} />
							<div className="min-w-0">
								<p className="truncate text-sm font-bold">
									{row.name}
								</p>
								<p className="truncate text-xs text-muted-foreground">
									{row.domain}
								</p>
							</div>
						</div>
						<dl className="mt-4 grid grid-cols-2 gap-3">
							<div>
								<dt className="text-[11px] text-muted-foreground">
									{t("estTraffic")}
								</dt>
								<dd className="font-mono text-xl font-extrabold tabular-nums">
									{row.traffic}
								</dd>
							</div>
							<div>
								<dt className="text-[11px] text-muted-foreground">
									{t("domainRank")}
								</dt>
								<dd className="font-mono text-xl font-extrabold tabular-nums">
									{row.domainRank}
								</dd>
							</div>
						</dl>
						<div className="mt-4 space-y-2 border-t pt-4">
							<div className="flex items-center justify-between gap-2">
								<span className="text-xs text-muted-foreground">
									{t("colAipvs")}
								</span>
								<ScoredTier
									score={row.aipvs.score}
									tier={row.aipvs.tier}
								/>
							</div>
							<div className="flex items-center justify-between gap-2">
								<span className="text-xs text-muted-foreground">
									{t("aiCitationsWindow", {
										days: pitchList.windowDays,
									})}
								</span>
								<span className="font-mono text-sm font-semibold tabular-nums">
									{row.citations}
								</span>
							</div>
							<div className="pt-1">
								{row.blocksAi ? (
									<BlocksAiLabel />
								) : (
									<span className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
										<CheckIcon
											className="size-3"
											aria-hidden
										/>
										{t("openToAi")}
									</span>
								)}
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

// ── Build: four ways in ─────────────────────────────────────────────────────

const SOURCES = [
	{ key: "search", icon: SearchIcon },
	{ key: "paste", icon: ClipboardPasteIcon },
	{ key: "lookup", icon: TelescopeIcon },
	{ key: "gaps", icon: TargetIcon },
] as const;

export function PitchSourcesTile({ className }: { className?: string }) {
	const t = useTranslations(NS);
	const revealed = useRevealed();
	return (
		<div className={cn("@container space-y-4", className)}>
			<ul className="grid grid-cols-2 gap-2">
				{SOURCES.map((source, i) => {
					const active = i === SOURCES.length - 1;
					return (
						<li
							key={source.key}
							className={cn(
								"flex min-w-0 items-start gap-2 rounded-xl border px-3 py-2.5",
								active
									? "border-primary/40 bg-primary/5"
									: "bg-background",
							)}
						>
							<source.icon
								className={cn(
									"mt-0.5 size-4 shrink-0",
									active
										? "text-primary"
										: "text-muted-foreground",
								)}
								aria-hidden
							/>
							<span className="min-w-0">
								<span className="block text-xs font-semibold text-foreground">
									{t(`sources.${source.key}.label`)}
								</span>
								<span className="block text-[11px] leading-snug text-muted-foreground">
									{t(`sources.${source.key}.hint`)}
								</span>
							</span>
						</li>
					);
				})}
			</ul>

			<div className="rounded-xl border bg-background">
				<p className="border-b px-3 py-2 text-[11px] font-semibold text-muted-foreground">
					{t("gapsHeader")}
				</p>
				<ul className="divide-y">
					{earnedMediaGaps.map((gap, i) => (
						<li
							key={gap.domain}
							className={cn(
								"flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5 @sm:flex-nowrap motion-safe:transition-opacity motion-safe:duration-500",
								revealed
									? "motion-safe:opacity-100"
									: "motion-safe:opacity-0",
							)}
							style={{ transitionDelay: `${200 + i * 120}ms` }}
						>
							<span
								className="flex size-4 shrink-0 items-center justify-center rounded-sm border border-primary bg-primary text-primary-foreground"
								aria-hidden
							>
								<CheckIcon className="size-3" />
							</span>
							<div className="min-w-0 flex-1">
								<p className="truncate text-sm font-medium">
									{gap.domain}
								</p>
								<p className="truncate text-[11px] text-muted-foreground">
									{t("citedFor", { names: gap.citesFor })}
								</p>
							</div>
							<span className="basis-full pl-7 @sm:basis-auto @sm:pl-0">
								<ScoredTier
									score={gap.aipvs.score}
									tier={gap.aipvs.tier}
									className="shrink-0"
								/>
							</span>
						</li>
					))}
				</ul>
				<div className="flex items-center justify-between gap-2 border-t px-3 py-2.5">
					<span className="text-[11px] text-muted-foreground">
						{t("selected", { count: earnedMediaGaps.length })}
					</span>
					<span className="rounded-md bg-primary px-2.5 py-1 text-xs font-bold text-primary-foreground">
						{t("addToList")}
					</span>
				</div>
			</div>
		</div>
	);
}

// ── Work: the status pipeline + the Placed suggestion ───────────────────────

export function PitchWorkTile({ className }: { className?: string }) {
	const t = useTranslations(NS);
	const row = pitchRows.find((r) => r.id === placedNudge.rowId);
	if (!row) return null;
	const stages: PitchStatusKey[] = ["target", "pitched", "placed"];
	return (
		<div className={cn("@container space-y-5", className)}>
			{/* Pipeline: always one compact row; boxes shrink, never stack. */}
			<div>
				<ol className="flex items-center gap-1 @xs:gap-1.5">
					{stages.map((stage, i) => (
						<Fragment key={stage}>
							{i > 0 ? (
								<li aria-hidden className="shrink-0">
									<ArrowRightIcon className="size-3.5 text-muted-foreground" />
								</li>
							) : null}
							<li className="min-w-0 flex-1 rounded-xl border bg-background px-1.5 py-2 text-center @xs:px-2 @xs:py-2.5">
								<p className="font-mono text-lg font-extrabold tabular-nums @xs:text-xl">
									{pitchStatusCounts[stage]}
								</p>
								<div className="mt-1 flex justify-center">
									<PitchStatusChip status={stage} />
								</div>
							</li>
						</Fragment>
					))}
				</ol>
				<p className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
					<span className="rounded-full border border-dashed px-2 py-px font-semibold">
						{t("declinedCount", {
							count: pitchStatusCounts.declined,
						})}
					</span>
					{t("declinedKept")}
				</p>
			</div>

			{/* One row, mid-pipeline, with the suggestion */}
			<div className="rounded-xl border bg-background p-3.5">
				<div className="flex flex-col gap-2.5 @sm:flex-row @sm:items-start @sm:justify-between @sm:gap-3">
					<div className="flex min-w-0 items-center gap-2.5">
						<PublisherMark name={row.name} />
						<div className="min-w-0">
							<p className="truncate text-sm font-medium">
								{row.name}
							</p>
							<p className="truncate text-xs text-muted-foreground">
								{row.domain}
							</p>
						</div>
					</div>
					<div className="flex shrink-0 flex-wrap items-center gap-2 pl-[2.375rem] @sm:flex-col @sm:items-end @sm:gap-1.5 @sm:pl-0">
						<PitchStatusChip status={row.status} />
						<PlacedSuggestion />
					</div>
				</div>
				{row.note ? (
					<div className="mt-3 rounded-lg bg-muted/50 px-3 py-2">
						<p className="text-[11px] font-semibold text-muted-foreground">
							{t("note")}
						</p>
						<p className="text-xs text-foreground">
							{t(`notes.${row.note}`)}
						</p>
					</div>
				) : null}
				<p className="mt-3 flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
					<SparklesIcon
						className="size-3 shrink-0 text-primary"
						aria-hidden
					/>
					<span className="truncate">
						{t("found", {
							url: placedNudge.url,
							score: placedNudge.aipqs,
						})}
					</span>
				</p>
			</div>
		</div>
	);
}

/** The product's Placed nudge: a suggestion, never an automatic change. */
export function PlacedSuggestion({ className }: { className?: string }) {
	const t = useTranslations(NS);
	return (
		<span
			className={cn(
				"flex w-fit flex-col items-start gap-0.5 whitespace-nowrap rounded-md border border-dashed border-primary/40 bg-primary/5 px-2 py-1 text-[11px]",
				className,
			)}
		>
			<span className="inline-flex items-center gap-1 font-medium text-foreground">
				<SparklesIcon className="size-3 text-primary" aria-hidden />
				{t("placementFound")}
			</span>
			<span className="pl-4 font-semibold text-primary">
				{t("markPlaced")}
			</span>
		</span>
	);
}

// ── Share: the read-only client page ────────────────────────────────────────

function ShareKpi({ value, label }: { value: ReactNode; label: string }) {
	return (
		<div className="flex flex-col bg-card px-3 py-2.5">
			<dt className="order-2 text-[10px] leading-tight text-muted-foreground">
				{label}
			</dt>
			<dd className="order-1 font-mono text-lg font-extrabold tracking-tight">
				{value}
			</dd>
		</div>
	);
}

export function SharedPitchPageCard({ className }: { className?: string }) {
	const t = useTranslations(NS);
	const shown = pitchRows.filter((r) => r.note);
	return (
		<div
			className={cn(
				"@container overflow-hidden rounded-xl border bg-background shadow-sm",
				className,
			)}
		>
			<div className="flex items-center gap-2 border-b bg-muted/60 px-3 py-2">
				<span className="flex gap-1" aria-hidden>
					<span className="size-2 rounded-full bg-muted-foreground/25" />
					<span className="size-2 rounded-full bg-muted-foreground/25" />
					<span className="size-2 rounded-full bg-muted-foreground/25" />
				</span>
				<span className="inline-flex min-w-0 items-center gap-1.5 rounded-md border bg-background px-2 py-0.5 text-[11px] text-muted-foreground">
					<LockIcon className="size-3 shrink-0" aria-hidden />
					<span className="truncate">{t("readOnlyLink")}</span>
				</span>
			</div>

			<div className="space-y-3 p-4">
				<div className="flex items-center gap-3">
					<span
						className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 text-xs font-bold text-primary"
						aria-hidden
					>
						{pitchList.agency.initials}
					</span>
					<div className="min-w-0">
						<p className="text-[11px] font-medium uppercase tracking-wide text-primary">
							{t("sharedEyebrow")}
						</p>
						<p className="truncate text-base font-bold tracking-tight">
							{t("listName")}
						</p>
						<p className="text-[11px] text-muted-foreground">
							{t("sharedMeta", {
								brand: pitchList.brand,
								agency: pitchList.agency.name,
								date: t("preparedOn"),
							})}
						</p>
					</div>
				</div>

				<dl className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border @md:grid-cols-4">
					<ShareKpi
						value={pitchSummary.publishers}
						label={t("kpiPublishers")}
					/>
					<ShareKpi
						value={`${pitchSummary.alreadyCitedPct}%`}
						label={t("kpiAlreadyCited")}
					/>
					<ShareKpi
						value={pitchSummary.placed}
						label={t("kpiPlaced")}
					/>
					<ShareKpi
						value={pitchSummary.premiumStrong}
						label={t("kpiPremiumStrong")}
					/>
				</dl>

				<ul className="divide-y rounded-lg border">
					{shown.map((row) => (
						<li key={row.id} className="space-y-1 px-3 py-2">
							<div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
								<span className="text-xs font-semibold">
									{row.name}
								</span>
								<span className="flex shrink-0 items-center gap-2">
									<AipvsTierBadge tier={row.aipvs.tier} />
									<PitchStatusChip status={row.status} />
								</span>
							</div>
							{row.note ? (
								<p className="text-[11px] leading-snug text-muted-foreground">
									{t(`notes.${row.note}`)}
								</p>
							) : null}
						</li>
					))}
				</ul>

				<p className="text-[11px] font-semibold text-muted-foreground">
					{t("howToRead")}
				</p>
			</div>
		</div>
	);
}

/** The export pair that sits under the shared page in the Share tile. */
export function PitchExportChips({ className }: { className?: string }) {
	const t = useTranslations(NS);
	return (
		<div
			className={cn(
				"flex flex-wrap items-center gap-2 text-xs text-muted-foreground",
				className,
			)}
		>
			{t("orDownload")}
			{["PDF", "CSV"].map((format) => (
				<span
					key={format}
					className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-0.5 text-[11px] font-semibold text-foreground"
				>
					<FileDownIcon className="size-3" aria-hidden />
					{format}
				</span>
			))}
		</div>
	);
}
